/**
 * officeShim.ts
 *
 * Single seam between this app and Office.js. Every other module under
 * src/office (and everything upstream of it) talks to the Promise-based
 * `OfficeCtx` / `MailItemLike` interfaces below — never to the global
 * `Office` object directly. That keeps Office.js's callback+AsyncResult
 * style contained to this one file and makes the fake-office dev mode a
 * straightforward alternate implementation of the same interfaces.
 *
 * Real-Office.js property mapping note: Office.js's read-item surface has
 * no field literally named "sent date". We map EmailPayload's
 * `sentDateTime` from `item.dateTimeCreated`, the closest built-in
 * property. Treat it as best-effort client metadata only — the backend
 * parses the authoritative `Date:` header out of `headersRaw` when it
 * reconstructs the .eml.
 */

export interface MailItemAddress {
  displayName: string;
  emailAddress: string;
}

export interface MailItemAttachmentMeta {
  id: string;
  name: string;
  contentType: string;
  size: number;
  isInline: boolean;
  contentId: string | null;
}

export interface MailItemAttachmentContent {
  /** true when Office.js returned Base64-format bytes we can upload. */
  formatIsBase64: boolean;
  /** Base64 string when formatIsBase64 is true; otherwise unusable. */
  content: string;
}

export interface NewMessageFormOptions {
  toRecipients: string[];
  subject: string;
  htmlBody: string;
}

/** Structural subset of an Office.js read-mode mail item this app needs. */
export interface MailItemLike {
  internetMessageId: string;
  subject: string;
  sentDateTime: string; // ISO 8601
  from: MailItemAddress;
  to: MailItemAddress[];
  cc: MailItemAddress[];
  attachments: MailItemAttachmentMeta[];
  getAllInternetHeadersAsync(): Promise<string>;
  getHtmlBodyAsync(): Promise<string>;
  getAttachmentContentAsync(
    attachmentId: string,
  ): Promise<MailItemAttachmentContent>;
}

/**
 * Per-mailbox persistent settings.
 *
 * Office roaming settings, not localStorage: they follow the mailbox rather
 * than the device, so a preference set in desktop Outlook is already there in
 * OWA and survives a reinstall or a cleared webview cache. They also stay
 * correct once Egnyte OAuth replaces the shared dev identity — the store is
 * already scoped per user, with no backend state to migrate.
 *
 * `set` is in-memory until `save` resolves.
 */
export interface RoamingSettingsLike {
  get(key: string): unknown;
  set(key: string, value: unknown): void;
  save(): Promise<void>;
}

export interface OfficeCtx {
  isFake: boolean;
  roamingSettings: RoamingSettingsLike;
  mailbox: {
    getCurrentItem(): MailItemLike | null;
    addItemChangedHandler(handler: () => void): void;
    removeItemChangedHandler(): void;
    displayNewMessageForm(options: NewMessageFormOptions): void;
  };
}

export async function initOffice(): Promise<OfficeCtx> {
  if (import.meta.env.VITE_FAKE_OFFICE === "1") {
    return createFakeOfficeCtx();
  }

  const info = await Office.onReady();
  if (info.host !== Office.HostType.Outlook) {
    throw new Error(
      `This add-in only runs in Outlook (detected host: ${String(info.host)}).`,
    );
  }
  return createRealOfficeCtx();
}

// ---------------------------------------------------------------------------
// Real Office.js adapter
// ---------------------------------------------------------------------------

function promisifyOfficeCall<T>(
  run: (callback: (result: Office.AsyncResult<T>) => void) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    run((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value);
      } else {
        reject(
          new Error(result.error?.message ?? "Office.js async call failed"),
        );
      }
    });
  });
}

function toAddress(details: Office.EmailAddressDetails | undefined): MailItemAddress {
  return {
    displayName: details?.displayName ?? "",
    emailAddress: details?.emailAddress ?? "",
  };
}

function toAttachmentMeta(
  details: Office.AttachmentDetails,
): MailItemAttachmentMeta {
  return {
    id: details.id,
    name: details.name,
    contentType: details.contentType,
    size: details.size,
    isInline: details.isInline,
    contentId: details.contentId || null,
  };
}

function wrapRealItem(raw: Office.MessageRead): MailItemLike {
  return {
    internetMessageId: raw.internetMessageId ?? "",
    subject: raw.subject ?? "",
    sentDateTime: (raw.dateTimeCreated ?? new Date()).toISOString(),
    from: toAddress(raw.from),
    to: (raw.to ?? []).map(toAddress),
    cc: (raw.cc ?? []).map(toAddress),
    attachments: (raw.attachments ?? []).map(toAttachmentMeta),
    getAllInternetHeadersAsync: () =>
      promisifyOfficeCall<string>((cb) => {
        raw.getAllInternetHeadersAsync(cb);
      }),
    getHtmlBodyAsync: () =>
      promisifyOfficeCall<string>((cb) => {
        raw.body.getAsync(Office.CoercionType.Html, cb);
      }),
    getAttachmentContentAsync: (attachmentId: string) =>
      promisifyOfficeCall<Office.AttachmentContent>((cb) => {
        raw.getAttachmentContentAsync(attachmentId, cb);
      }).then((content) => ({
        formatIsBase64:
          content.format ===
          Office.MailboxEnums.AttachmentContentFormat.Base64,
        content: content.content,
      })),
  };
}

function createRealRoamingSettings(): RoamingSettingsLike {
  const settings = Office.context.roamingSettings;
  return {
    get: (key) => settings.get(key) as unknown,
    set: (key, value) => {
      settings.set(key, value);
    },
    save: () =>
      promisifyOfficeCall<void>((cb) => {
        settings.saveAsync(cb);
      }),
  };
}

function createRealOfficeCtx(): OfficeCtx {
  return {
    isFake: false,
    roamingSettings: createRealRoamingSettings(),
    mailbox: {
      getCurrentItem: () => {
        const raw = Office.context.mailbox.item as
          | Office.MessageRead
          | undefined;
        return raw ? wrapRealItem(raw) : null;
      },
      addItemChangedHandler: (handler) => {
        Office.context.mailbox.addHandlerAsync(
          Office.EventType.ItemChanged,
          () => handler(),
          (result) => {
            if (result.status !== Office.AsyncResultStatus.Succeeded) {
              console.error(
                "Failed to subscribe to ItemChanged",
                result.error,
              );
            }
          },
        );
      },
      removeItemChangedHandler: () => {
        Office.context.mailbox.removeHandlerAsync(
          Office.EventType.ItemChanged,
          {},
          (result) => {
            if (result.status !== Office.AsyncResultStatus.Succeeded) {
              console.error(
                "Failed to unsubscribe from ItemChanged",
                result.error,
              );
            }
          },
        );
      },
      displayNewMessageForm: (options) => {
        Office.context.mailbox.displayNewMessageForm(options);
      },
    },
  };
}

// ---------------------------------------------------------------------------
// Fake Office (VITE_FAKE_OFFICE=1) — pure-browser dev mode
// ---------------------------------------------------------------------------

/** Small deterministic PRNG (mulberry32) so fake attachment bytes are
 * stable across runs — useful if this ever backs a golden-file test. */
function seededBytes(seed: number, length: number): Uint8Array {
  let a = seed;
  const out = new Uint8Array(length);
  for (let i = 0; i < length; i++) {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    out[i] = ((t ^ (t >>> 14)) >>> 0) & 0xff;
  }
  return out;
}

function bytesToBase64(bytes: Uint8Array): string {
  const CHUNK = 8192;
  let binary = "";
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const slice = bytes.subarray(i, i + CHUNK);
    binary += String.fromCharCode(...slice);
  }
  return btoa(binary);
}

const FAKE_HTML_BODY = `
<html>
  <body style="font-family: Segoe UI, sans-serif; font-size: 14px; color: #1f2937;">
    <p>Hi Felix,</p>
    <p>Attached is the revised precast panel submittal package (Submittal 042)
    for the La Verkin project, incorporating the structural engineer's
    redline comments from last week's review call. Panel schedule is in the
    xlsx; full shop drawings and spec sheets are in the PDF.</p>
    <p>Please route this to the architect of record for stamped approval by
    end of week — we're holding a fabrication slot that starts to slip
    after Friday.</p>
    <p>Let me know if you need anything else from our end.</p>
    <p>Thanks,<br />Jordan</p>
    <p style="margin-top: 24px;">
      <img src="cid:logo01" alt="Company logo" width="140" height="40" />
    </p>
    <p style="font-size: 11px; color: #6b7280;">
      This message and any attachments are intended only for the
      addressee(s) named above and may contain confidential or privileged
      information. If you received this in error, please notify the sender
      and delete it. This is fake development content generated by
      officeShim's fake-office mode (VITE_FAKE_OFFICE=1) — it is not a real
      email and was never sent.
    </p>
  </body>
</html>
`.trim();

const FAKE_HEADERS_RAW = `Received: from mail.example.com (mail.example.com [203.0.113.10])
\tby mx.example.net with ESMTPS id fake0001
\tfor <felix@example.net>; Wed, 22 Jul 2026 14:12:03 -0400
From: "Jordan Blake" <jordan.blake@example.com>
To: "Felix Zayas" <felix@example.net>
Subject: RE: Submittal 042 =?UTF-8?Q?=E2=80=94?= Precast Panels
Date: Wed, 22 Jul 2026 14:12:00 -0400
Message-ID: <fake-123@example.com>
Thread-Topic: Submittal 042 — Precast Panels
MIME-Version: 1.0
Content-Type: multipart/related; boundary="----=_NextPart_fake_000_0001"
X-Mailer: FakeOffice/1.0 (dev, VITE_FAKE_OFFICE=1)`;

function createFakeAttachments(): {
  metas: MailItemAttachmentMeta[];
  bytes: Map<string, Uint8Array>;
} {
  const pdfBytes = seededBytes(1, 180 * 1024);
  const xlsxBytes = seededBytes(2, 45 * 1024);
  const pngBytes = seededBytes(3, 3 * 1024);

  const metas: MailItemAttachmentMeta[] = [
    {
      id: "fake-att-1",
      name: "Precast_Panels_Submittal_042.pdf",
      contentType: "application/pdf",
      size: pdfBytes.length,
      isInline: false,
      contentId: null,
    },
    {
      id: "fake-att-2",
      name: "Panel_Schedule_042.xlsx",
      contentType:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      size: xlsxBytes.length,
      isInline: false,
      contentId: null,
    },
    {
      id: "fake-att-3",
      name: "logo.png",
      contentType: "image/png",
      size: pngBytes.length,
      isInline: true,
      contentId: "logo01",
    },
  ];

  const bytes = new Map<string, Uint8Array>([
    ["fake-att-1", pdfBytes],
    ["fake-att-2", xlsxBytes],
    ["fake-att-3", pngBytes],
  ]);

  return { metas, bytes };
}

function createFakeItem(): MailItemLike {
  const { metas, bytes } = createFakeAttachments();

  return {
    internetMessageId: "<fake-123@example.com>",
    subject: "RE: Submittal 042 — Precast Panels",
    sentDateTime: "2026-07-22T18:12:00.000Z",
    from: {
      displayName: "Jordan Blake",
      emailAddress: "jordan.blake@example.com",
    },
    to: [{ displayName: "Felix Zayas", emailAddress: "felix@example.net" }],
    cc: [],
    attachments: metas,
    getAllInternetHeadersAsync: () => Promise.resolve(FAKE_HEADERS_RAW),
    getHtmlBodyAsync: () => Promise.resolve(FAKE_HTML_BODY),
    getAttachmentContentAsync: (attachmentId: string) => {
      const content = bytes.get(attachmentId);
      if (!content) {
        return Promise.reject(
          new Error(`FakeOffice: unknown attachment id "${attachmentId}"`),
        );
      }
      return Promise.resolve({
        formatIsBase64: true,
        content: bytesToBase64(content),
      });
    },
  };
}

const FAKE_ROAMING_STORAGE_KEY = "filer.fakeRoamingSettings";

/** localStorage-backed stand-in so settings survive a dev-mode page reload. */
function createFakeRoamingSettings(): RoamingSettingsLike {
  let store: Record<string, unknown> = {};
  try {
    const raw = localStorage.getItem(FAKE_ROAMING_STORAGE_KEY);
    if (raw) {
      store = JSON.parse(raw) as Record<string, unknown>;
    }
  } catch {
    store = {};
  }

  return {
    get: (key) => store[key],
    set: (key, value) => {
      store[key] = value;
    },
    save: () => {
      localStorage.setItem(FAKE_ROAMING_STORAGE_KEY, JSON.stringify(store));
      return Promise.resolve();
    },
  };
}

function createFakeOfficeCtx(): OfficeCtx {
  const item = createFakeItem();

  return {
    isFake: true,
    roamingSettings: createFakeRoamingSettings(),
    mailbox: {
      getCurrentItem: () => item,
      // FakeOffice never fires ItemChanged — there is exactly one fake
      // item for the lifetime of a browser dev session and no host to
      // change selection out from under it.
      addItemChangedHandler: () => undefined,
      removeItemChangedHandler: () => undefined,
      displayNewMessageForm: (options) => {
        console.log("[FakeOffice] displayNewMessageForm called with:", options);
      },
    },
  };
}
